'use client';

import { useRef, useState } from 'react';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Icon from '@/components/ui/AppIcon';

import MobileServiceCatalog from './MobileServiceCatalog';

if (typeof window !== 'undefined') {
    gsap.registerPlugin(ScrollTrigger);
}

const servicesData = [
    {
        id: 'web-dev',
        title: 'Web Development Studio',
        description: 'Full-stack web solutions with cutting-edge technologies and responsive design that delivers exceptional user experiences.',
        icon: 'CodeBracketIcon',
        color: 'from-blue-500 to-indigo-600',
        shadow: 'shadow-blue-500/20',
        geom: 'cube',
        features: [
            'Custom Web Applications',
            'Responsive Design',
            'Performance Optimization',
            'UI/UX Excellence',
            'Scalable Cloud Architecture',
            'Secure API Integration',
            'E-commerce Solutions',
            'Progressive Web Apps (PWA)'
        ]
    },
    {
        id: 'mobile-app',
        title: 'Mobile App Development',
        description: 'Native and cross-platform mobile applications that engage users with intuitive interfaces and seamless performance.',
        icon: 'DevicePhoneMobileIcon',
        color: 'from-violet-500 to-purple-600',
        shadow: 'shadow-violet-500/20',
        geom: 'sphere',
        features: [
            'Native Android & iOS Apps',
            'Cross-Platform (Flutter/React)',
            'App Store Optimization',
            'Mobile-First Design',
            'Real-time Features & Chat',
            'Push Notifications',
            'Offline Capabilities',
            'Wearable App Integration'
        ]
    },
    {
        id: 'brand-lab',
        title: 'Brand Visibility Lab',
        description: 'Strategic brand optimization services identifying high-potential opportunities to maximize your online presence.',
        icon: 'ChartBarIcon',
        color: 'from-amber-500 to-orange-600',
        shadow: 'shadow-amber-500/20',
        geom: 'pyramid',
        features: [
            'Google Business Profile',
            'Advanced SEO Strategy',
            'Reputation Management',
            'Local Search Dominance',
            'Competitor Analysis',
            'Conversion Optimization',
            'Content Marketing',
            'Analytics & Reporting'
        ]
    },
    {
        id: 'ecosystem',
        title: 'Complete Digital Ecosystem',
        description: 'End-to-end digital transformation combining web, mobile, and brand strategies for comprehensive business growth.',
        icon: 'RocketLaunchIcon',
        color: 'from-emerald-500 to-teal-600',
        shadow: 'shadow-emerald-500/20',
        geom: 'torus',
        features: [
            'Free project consultation',
            'Custom strategy roadmap',
            'No-obligation proposal',
            'Quick 24-hour response'
        ]
    }
];

const ServiceCatalog = () => {
    const containerRef = useRef<HTMLElement>(null);
    // Track expanded state by ID instead of full object for simpler logic
    const [expandedId, setExpandedId] = useState<string | null>(null);

    useGSAP(() => {
        const cards = gsap.utils.toArray<HTMLElement>('.service-prism');

        gsap.fromTo(cards,
            { y: 100, opacity: 0, rotateX: 20 },
            {
                y: 0,
                opacity: 1,
                rotateX: 0,
                duration: 1,
                stagger: 0.1,
                ease: 'power3.out',
                scrollTrigger: {
                    trigger: containerRef.current,
                    start: 'top 75%',
                    end: 'bottom top',
                    toggleActions: 'play none none reverse'
                }
            }
        );
    }, { scope: containerRef });

    // Tilt Logic
    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>, idx: number) => {
        if (expandedId !== null) return; // Disable tilt when interacting with details

        const card = e.currentTarget;
        const box = card.getBoundingClientRect();
        const x = e.clientX - box.left;
        const y = e.clientY - box.top;
        const centerX = box.width / 2;
        const centerY = box.height / 2;

        const rotateX = ((y - centerY) / centerY) * -10; // Max 10deg rotation
        const rotateY = ((x - centerX) / centerX) * 10;

        gsap.to(card, {
            rotateX: rotateX,
            rotateY: rotateY,
            scale: 1.02,
            duration: 0.5,
            ease: 'power2.out'
        });

        // Move inner 3D elements
        gsap.to(card.querySelectorAll('.parallax-layer'), {
            x: (x - centerX) / 10,
            y: (y - centerY) / 10,
            duration: 0.5,
            ease: 'power2.out'
        });
    };

    const handleMouseLeave = (e: React.MouseEvent<HTMLDivElement>) => {
        gsap.to(e.currentTarget, { rotateX: 0, rotateY: 0, scale: 1, duration: 0.5 });
        gsap.to(e.currentTarget.querySelectorAll('.parallax-layer'), { x: 0, y: 0, duration: 0.5 });
    };

    const render3DShape = (type: string, color: string) => {
        const baseClass = "absolute w-full h-full preserve-3d opacity-30 animate-[spin_8s_linear_infinite]";
        // Simplified CSS 3D shapes
        switch (type) {
            case 'cube':
                return (
                    <div className={`${baseClass}`}>
                        <div className="absolute inset-0 bg-blue-500/20 translate-z-4 border border-blue-400" />
                        <div className="absolute inset-0 bg-blue-500/20 -translate-z-4 border border-blue-400" />
                        <div className="absolute inset-0 bg-indigo-500/20 rotate-y-90 translate-z-4 border border-indigo-400" />
                        <div className="absolute inset-0 bg-indigo-500/20 -rotate-y-90 translate-z-4 border border-indigo-400" />
                    </div>
                );
            case 'pyramid':
                // Represented as a rotating diamond/octahedron for CSS simplicity
                return (
                    <div className={`${baseClass}`}>
                        <div className="absolute inset-0 bg-amber-500/20 rotate-45 transform skew-x-12 border border-amber-400" />
                        <div className="absolute inset-0 bg-orange-500/20 -rotate-45 transform -skew-y-12 border border-orange-400" />
                    </div>
                );
            default: // Sphere/Torus fallback to circle stack
                return (
                    <div className={`${baseClass}`}>
                        <div className="absolute inset-0 rounded-full border-2 border-dashed border-slate-400/50 rotate-x-45" />
                        <div className="absolute inset-0 rounded-full border-2 border-dashed border-slate-400/50 rotate-y-45" />
                        <div className="absolute inset-0 rounded-full border-2 border-slate-400/20" />
                    </div>
                );
        }
    };

    return (
        <section ref={containerRef} className="relative z-10 perspective-2000 overflow-visible">

            {/* Desktop View */}
            <div className="hidden lg:block py-32 container mx-auto px-4">
                <div className="text-center mb-20 relative z-10">
                    <span className="inline-block px-4 py-1.5 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-600 text-xs font-bold uppercase tracking-wider mb-6 animate-pulse">
                        World Class Solutions
                    </span>
                    <h2 className="text-5xl md:text-7xl font-black text-slate-900 mb-6 tracking-tight">
                        Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600">Services</span>
                    </h2>
                    <p className="text-xl text-slate-600 max-w-2xl mx-auto">
                        Meticulously crafted digital experiences designed to define the future of your brand.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-16 max-w-7xl mx-auto">
                    {servicesData.map((service, idx) => {
                        const isExpanded = expandedId === service.id;

                        return (
                            <div
                                key={idx}
                                className={`service-prism group relative min-h-[420px] preserve-3d cursor-pointer ${isExpanded ? 'active-card z-20' : ''}`}
                                onMouseMove={(e) => handleMouseMove(e, idx)}
                                onMouseLeave={handleMouseLeave}
                            >
                                {/* Card Body */}
                                <div className={`${isExpanded ? 'relative h-auto' : 'absolute inset-0'} bg-white/40 backdrop-blur-xl rounded-[2.5rem] border border-white/60 ${service.shadow} transition-all duration-500 group-hover:shadow-2xl overflow-hidden`}>

                                    {/* Dynamic Gradient Background */}
                                    <div className={`absolute -right-20 -top-20 w-80 h-80 bg-gradient-to-br ${service.color} opacity-10 blur-3xl rounded-full group-hover:opacity-20 transition-opacity duration-500`} />
                                    <div className={`absolute -left-20 -bottom-20 w-60 h-60 bg-gradient-to-tr ${service.color} opacity-10 blur-3xl rounded-full group-hover:opacity-20 transition-opacity duration-500`} />

                                    <div className="relative h-full p-10 flex flex-col z-10">

                                        {/* Header Area (Icon + Shapes) - Always Visible but scales down if needed? No, kept consistent. */}
                                        <div className="parallax-layer w-20 h-20 mb-8 relative preserve-3d shrink-0">
                                            <div className={`absolute inset-0 bg-gradient-to-br ${service.color} rounded-2xl opacity-20 transform rotate-6 translate-z-2`} />
                                            <div className={`absolute inset-0 bg-white rounded-2xl shadow-lg border border-white/50 flex items-center justify-center transform -rotate-3 translate-z-4 group-hover:rotate-0 transition-transform duration-500`}>
                                                <Icon name={service.icon as any} className="w-10 h-10 text-slate-800" />
                                            </div>

                                            {/* Background 3D Shape specific to service */}
                                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 -z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
                                                {render3DShape(service.geom, service.color)}
                                            </div>
                                        </div>

                                        {/* CONTENT SWAP AREA */}
                                        <div className="relative flex-1">

                                            {/* Default View: Title + Desc + Button */}
                                            <div
                                                className={`transition-all duration-500 ${isExpanded ? 'hidden' : 'flex flex-col h-full'}`}
                                            >
                                                <h3 className="parallax-layer text-3xl font-black text-slate-900 mb-4 translate-z-6">{service.title}</h3>
                                                <p className="parallax-layer text-slate-600 text-lg leading-relaxed mb-8 translate-z-4 flex-1">
                                                    {service.description}
                                                </p>

                                                <div className="parallax-layer mt-auto translate-z-8 self-start">
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setExpandedId(service.id);
                                                        }}
                                                        className="group/btn relative px-8 py-3 rounded-xl bg-slate-900 text-white font-bold overflow-hidden"
                                                    >
                                                        <span className="relative z-10 flex items-center gap-2">
                                                            View Features
                                                            <Icon name="ArrowRightIcon" className="w-4 h-4 transform group-hover/btn:translate-x-1 transition-transform" />
                                                        </span>
                                                        <div className={`absolute inset-0 bg-gradient-to-r ${service.color} opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300`} />
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Expanded View: Features List + Close Button */}
                                            {/* We use minimal absolute positioning/hiding to allow layout flow when expanded */}
                                            <div
                                                className={`transition-all duration-500 flex flex-col ${isExpanded ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 absolute inset-0 pointer-events-none'}`}
                                            >
                                                <div className="flex items-center justify-between mb-6">
                                                    <h3 className="text-2xl font-black text-slate-900 leading-none">{service.title}</h3>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setExpandedId(null);
                                                        }}
                                                        className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-500 transition-colors"
                                                    >
                                                        <Icon name="XMarkIcon" className="w-4 h-4" />
                                                    </button>
                                                </div>

                                                <div className="space-y-3 mb-6">
                                                    {service.features.map((feature, i) => (
                                                        <div key={i} className="flex items-start space-x-3 p-3 rounded-xl bg-white/50 border border-slate-100">
                                                            <Icon name="CheckCircleIcon" className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
                                                            <span className="font-medium text-slate-700 text-sm leading-snug">{feature}</span>
                                                        </div>
                                                    ))}
                                                </div>

                                                <div className="pt-4 border-t border-slate-100/50 text-center mt-auto">
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setExpandedId(null);
                                                        }}
                                                        className="w-full py-3 bg-slate-100 text-slate-600 font-bold rounded-xl hover:bg-slate-200 transition-colors flex items-center justify-center gap-2 text-sm"
                                                    >
                                                        Close & Continue Exploring
                                                        <Icon name="ArrowUturnLeftIcon" className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>

                                        </div>

                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Mobile View */}
            <div className="block lg:hidden">
                <MobileServiceCatalog />
            </div>
        </section>
    );
};

export default ServiceCatalog;

